import type { StyleSpecification } from 'maplibre-gl';

/**
 * Basemap is pluggable on purpose.
 *
 * Golfers expect satellite imagery. Satellite imagery is never free at fleet
 * scale, so the provider is an env var and the operator plugs in whichever
 * licence they buy. `esri` is fine for development and demos; ship on a paid
 * key. See docs/MERCURY_FAIRWAY_MAP_LICENSING.md.
 */
export type BasemapProvider = 'maptiler' | 'esri' | 'osm' | 'blank';

const provider = (import.meta.env.VITE_BASEMAP ?? 'esri') as BasemapProvider;
const maptilerKey = import.meta.env.VITE_MAPTILER_KEY ?? '';

const RASTER = (tiles: string[], attribution: string, maxzoom = 19): StyleSpecification => ({
  version: 8,
  sources: {
    base: { type: 'raster', tiles, tileSize: 256, maxzoom, attribution },
  },
  layers: [
    { id: 'bg', type: 'background', paint: { 'background-color': '#0b120d' } },
    { id: 'base', type: 'raster', source: 'base', paint: { 'raster-brightness-max': 0.92 } },
  ],
});

export function basemapStyle(): StyleSpecification | string {
  switch (provider) {
    case 'maptiler':
      if (!maptilerKey) break;
      return `https://api.maptiler.com/maps/satellite/style.json?key=${maptilerKey}`;
    case 'osm':
      return RASTER(
        ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
        '© OpenStreetMap contributors'
      );
    case 'esri':
      return RASTER(
        [
          'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        ],
        'Imagery © Esri, Maxar, Earthstar Geographics'
      );
    case 'blank':
      break;
  }
  // No imagery: draw the hole on a flat green. Still fully usable — the
  // yardages are GPS maths, not map maths.
  return {
    version: 8,
    sources: {},
    layers: [{ id: 'bg', type: 'background', paint: { 'background-color': '#16351f' } }],
  };
}

export const basemapProvider = provider;

/**
 * Road basemap for the navigation screen, in the caller's theme.
 *
 * Satellite is right for a golf hole — the golfer needs to see the actual
 * bunker. It is wrong for navigating: at speed you need road names and a clear
 * hierarchy of streets, and aerial photography gives neither.
 *
 * CARTO's basemaps rather than a keyed provider: free, no signup, and the two
 * variants are the grey/blue palette a navigation app wants rather than the
 * primary-coloured OSM default. The app has to work the day somebody else
 * installs it, without provisioning anything.
 */
/**
 * What the one map is showing.
 *
 * `poly` is the golf-native one and has no imagery at all: the hole drawn as
 * pure geometry on flat ground. Aerial photography of a fairway is visually
 * busy — mown stripes, cart-path shadows, tree canopy overhanging the line —
 * and none of it is information. Stripping it leaves corridor, green, hazards
 * and cart path as the only marks on screen, which is the most legible the
 * hole ever gets and the cheapest to render on a weak head unit.
 */
export type MapMode = 'poly' | 'road' | 'satellite' | 'hybrid';

/** Esri World Imagery — the same aerial the hole view uses. */
const IMAGERY = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

/**
 * Esri Wayback — the SAME imagery, but from a chosen capture date.
 *
 * The current aerial of a course is whatever happened to be flown last, and
 * that is often mid-renovation: brown scars where a bunker was rebuilt, a dirt
 * haul road across the 3rd, greens under cover. Wayback keeps ~196 dated
 * releases back to 2014, so a course can be pinned to the pass where it looks
 * the way it actually plays. Same licence and attribution as World Imagery.
 *
 * Release ids come from the published config and are stable:
 *   https://s3-us-west-2.amazonaws.com/config.maptiles.arcgis.com/waybackconfig.json
 *
 * 🟥 Wayback is {level}/{row}/{col} — z/y/x — NOT the z/x/y most raster
 * services use. Swapping them silently serves tiles from the wrong place.
 */
export const waybackTiles = (releaseId: number | string) =>
  `https://wayback.maptiles.arcgis.com/arcgis/rest/services/World_Imagery/WMTS/1.0.0/default028mm/MapServer/tile/${releaseId}/{z}/{y}/{x}`;

/** Imagery for a course: its pinned vintage if it has one, else today's. */
export const imageryTilesFor = (releaseId?: number | string | null) =>
  releaseId ? waybackTiles(releaseId) : IMAGERY;

/** Base raster layer ids on the one map. Overlays are added after, so above. */
export const BM = { imagery: 'bm-imagery', roads: 'bm-roads', labels: 'bm-labels' } as const;

/**
 * The style for THE map — the only one.
 *
 * GoCaddy used to run two maps: a street map for driving to the course and a
 * satellite map for the hole. Two MapLibre instances, two code paths, and a
 * golfer who sees "two different maps" in one product. This is one map that
 * changes what it is showing.
 *
 * 🟥 WHY VISIBILITY AND NOT setStyle. NavApp swaps basemaps with `setStyle()`,
 * which is correct when the map carries nothing — it discards every source and
 * layer and rebuilds. The hole map carries terrain, the corridor mask, greens,
 * hazards, cart paths, the route and the fleet, so a `setStyle()` mid-round
 * would drop all of it and need every layer re-added in the right order. All
 * three basemaps therefore ship in the style from the start and the mode flips
 * `visibility`. Nothing is ever torn down, and switching mode costs no tiles
 * that have already been fetched.
 */
export function oneMapStyle(theme: 'dark' | 'light', mode: MapMode): StyleSpecification {
  const vis = (on: boolean) => ({ visibility: (on ? 'visible' : 'none') as 'visible' | 'none' });
  const imagery = mode === 'satellite' || mode === 'hybrid';
  // Ground under `poly`: turf, not chrome. Themed, because the head unit is a
  // sunlight device and a black field in poly mode would be a mirror at noon.
  const ground = theme === 'light' ? '#dfe7df' : '#14241a';

  return {
    version: 8,
    sources: {
      [BM.imagery]: {
        type: 'raster',
        tiles: [IMAGERY],
        tileSize: 256,
        maxzoom: 19,
        attribution: 'Imagery © Esri, Maxar, Earthstar Geographics',
      },
      [BM.roads]: {
        type: 'raster',
        tiles: [cartoTiles(theme === 'dark' ? 'dark_all' : 'light_all')],
        tileSize: 256,
        maxzoom: 20,
        attribution: '© OpenStreetMap contributors © CARTO',
      },
      [BM.labels]: {
        type: 'raster',
        tiles: [cartoTiles(theme === 'dark' ? 'dark_only_labels' : 'light_only_labels')],
        tileSize: 256,
        maxzoom: 20,
        attribution: '© OpenStreetMap contributors © CARTO',
      },
    },
    layers: [
      { id: 'bg', type: 'background', paint: { 'background-color': ground } },
      { id: BM.imagery, type: 'raster', source: BM.imagery, layout: vis(imagery),
        paint: { 'raster-brightness-max': 0.92 } },
      { id: BM.roads, type: 'raster', source: BM.roads, layout: vis(mode === 'road') },
      { id: BM.labels, type: 'raster', source: BM.labels, layout: vis(mode === 'hybrid') },
    ],
  };
}

/** Flip the one map between road / satellite / hybrid. No teardown. */
export function applyMapMode(m: import('maplibre-gl').Map, mode: MapMode): void {
  const set = (id: string, on: boolean) => {
    if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
  };
  set(BM.imagery, mode === 'satellite' || mode === 'hybrid');
  set(BM.roads, mode === 'road');
  set(BM.labels, mode === 'hybrid');
}

const cartoTiles = (variant: string) =>
  `https://a.basemaps.cartocdn.com/rastertiles/${variant}/{z}/{x}/{y}{ratio}.png`.replace(
    '{ratio}',
    devicePixelRatio > 1.5 ? '@2x' : ''
  );

/**
 * Basemap for the navigation screen, in the caller's theme and mode.
 *
 * Satellite is right for a golf hole — the golfer needs to see the actual
 * bunker. It is wrong for navigating: at speed you need road names and a clear
 * hierarchy of streets, and aerial photography gives neither. Which is why the
 * choice belongs to the golfer rather than to us, and why HYBRID exists: on a
 * course you want to see the fairway AND read the path you are on, and neither
 * of the other two gives you both.
 *
 * CARTO for the road layers rather than a keyed provider: free, no signup, and
 * the palette is the grey/blue a navigation app wants rather than the
 * primary-coloured OSM default. The app has to work the day somebody else
 * installs it, without provisioning anything.
 */
export function navBasemapStyle(theme: 'dark' | 'light', mode: MapMode = 'road'): StyleSpecification {
  const dark = theme === 'dark';
  const bg = dark ? '#0d1117' : '#eef1f4';

  if (mode === 'satellite') {
    return {
      version: 8,
      sources: {
        imagery: {
          type: 'raster',
          tiles: [IMAGERY],
          tileSize: 256,
          maxzoom: 19,
          attribution: 'Imagery © Esri, Maxar, Earthstar Geographics',
        },
      },
      layers: [
        { id: 'bg', type: 'background', paint: { 'background-color': bg } },
        { id: 'imagery', type: 'raster', source: 'imagery' },
      ],
    };
  }

  if (mode === 'hybrid') {
    // Labels only, so the imagery underneath stays readable. CARTO ships this
    // variant precisely for stacking over aerial.
    const labels = dark ? 'dark_only_labels' : 'light_only_labels';
    return {
      version: 8,
      sources: {
        imagery: {
          type: 'raster',
          tiles: [IMAGERY],
          tileSize: 256,
          maxzoom: 19,
          attribution: 'Imagery © Esri, Maxar, Earthstar Geographics',
        },
        labels: {
          type: 'raster',
          tiles: [cartoTiles(labels)],
          tileSize: 256,
          maxzoom: 20,
          attribution: '© OpenStreetMap contributors © CARTO',
        },
      },
      layers: [
        { id: 'bg', type: 'background', paint: { 'background-color': bg } },
        { id: 'imagery', type: 'raster', source: 'imagery' },
        { id: 'labels', type: 'raster', source: 'labels' },
      ],
    };
  }

  return {
    version: 8,
    sources: {
      roads: {
        type: 'raster',
        tiles: [cartoTiles(dark ? 'dark_all' : 'light_all')],
        tileSize: 256,
        maxzoom: 20,
        attribution: '© OpenStreetMap contributors © CARTO',
      },
    },
    layers: [
      { id: 'bg', type: 'background', paint: { 'background-color': bg } },
      { id: 'roads', type: 'raster', source: 'roads' },
    ],
  };
}
